import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronRight, Send, Loader2, Copy, Check, Edit2, Save, X, Upload, FileText, CheckCircle, XCircle, ScanText, Trash2, Plus, Trash, ToggleLeft, ToggleRight } from 'lucide-react';
import Header from '../components/Layout/Header';
import ChatMessage from '../components/Chat/ChatMessage';
import GeminiVoiceChat from '../components/Voice/GeminiVoiceChat';
import { Agent, Message } from '../types';
import { agentService, queryService } from '../services/api';
import { API_CONFIG, ADMIN_KEY } from '../config/api';
import toast from 'react-hot-toast';
import axios from 'axios';

const AgentExplorerPage: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'instructions' | 'capabilities' | 'chat' | 'pro' | 'voice' | 'widget'>('overview');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionId = useRef(`session-${Date.now()}`);

  // Edit instructions state
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [editedInstructions, setEditedInstructions] = useState<string>('');
  const [savingInstructions, setSavingInstructions] = useState(false);
  // OCR state for test chat
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrText, setOcrText] = useState<string | null>(null);
  const [ocrConfirmed, setOcrConfirmed] = useState(false);
  const [ocrPreview, setOcrPreview] = useState<string | null>(null);
  const [ocrAnalysis, setOcrAnalysis] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const CORE_API = API_CONFIG.core;
  const hasOcr = selectedAgent?.capabilities?.includes('ocr');

  // Pro chat state
  const [proMessages, setProMessages] = useState<Message[]>([]);
  const [proInput, setProInput] = useState('');
  const [proSending, setProSending] = useState(false);
  const [proStatus, setProStatus] = useState<string>('');
  const proMessagesEndRef = useRef<HTMLDivElement>(null);

  // Capabilities editing state
  const [isEditingCapabilities, setIsEditingCapabilities] = useState(false);
  const [editedCapabilities, setEditedCapabilities] = useState<string[]>([]);
  const [newCapability, setNewCapability] = useState('');
  const [savingCapabilities, setSavingCapabilities] = useState(false);

  // Well-known capabilities for quick toggle
  const KNOWN_CAPABILITIES = [
    { id: 'voice', label: 'Voice', icon: '🎤', description: 'Voice conversation via Gemini Live' },
    { id: 'ocr', label: 'OCR', icon: '📄', description: 'Document text extraction' },
    { id: 'legal-analysis', label: 'Legal Analysis', icon: '📊', description: 'Legal case analysis' },
    { id: 'document-drafting', label: 'Document Drafting', icon: '✍️', description: 'Draft legal documents' },
    { id: 'case-research', label: 'Case Research', icon: '🔍', description: 'Research case law' },
    { id: 'contract-review', label: 'Contract Review', icon: '📋', description: 'Review contracts' },
    { id: 'multilingual', label: 'Multilingual', icon: '🌐', description: 'Multi-language support' },
    { id: 'compliance', label: 'Compliance', icon: '✅', description: 'Compliance advisory' },
  ];

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    proMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [proMessages]);

  const loadAgents = async () => {
    try {
      const data = await agentService.getAll('lawyer');
      setAgents(data);
    } catch (error) {
      toast.error('Failed to load agents');
    }
  };

  const startEditingInstructions = () => {
    setEditedInstructions(selectedAgent?.systemPrompt || '');
    setIsEditingInstructions(true);
  };

  const cancelEditingInstructions = () => {
    setIsEditingInstructions(false);
    setEditedInstructions('');
  };

  const saveEditedInstructions = async () => {
    if (!selectedAgent) return;

    setSavingInstructions(true);
    try {
      const updateResponse = await axios.put(
        `${API_CONFIG.core}/api/admin/agents/${selectedAgent.id}`,
        {
          systemInstructions: editedInstructions,
          voiceInstructions: editedInstructions,
        },
        {
          headers: {
            'x-superadmin-key': ADMIN_KEY
          }
        }
      );

      if (updateResponse.data.success) {
        toast.success(`Instructions updated for ${selectedAgent.name}`);
        const updatedAgent = await agentService.getById(selectedAgent.id);
        setSelectedAgent(updatedAgent);
        setIsEditingInstructions(false);
        setEditedInstructions('');
      } else {
        throw new Error(updateResponse.data.error);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save instructions');
    } finally {
      setSavingInstructions(false);
    }
  };

  // ── Capabilities editing ──
  const startEditingCapabilities = () => {
    setEditedCapabilities([...(selectedAgent?.capabilities || [])]);
    setIsEditingCapabilities(true);
    setNewCapability('');
  };

  const cancelEditingCapabilities = () => {
    setIsEditingCapabilities(false);
    setEditedCapabilities([]);
    setNewCapability('');
  };

  const toggleCapability = (cap: string) => {
    setEditedCapabilities(prev =>
      prev.includes(cap) ? prev.filter(c => c !== cap) : [...prev, cap]
    );
  };

  const addCustomCapability = () => {
    const trimmed = newCapability.trim();
    if (!trimmed) return;
    if (editedCapabilities.includes(trimmed)) {
      toast.error('Capability already exists');
      return;
    }
    setEditedCapabilities(prev => [...prev, trimmed]);
    setNewCapability('');
  };

  const removeCapability = (cap: string) => {
    setEditedCapabilities(prev => prev.filter(c => c !== cap));
  };

  const saveCapabilities = async () => {
    if (!selectedAgent) return;
    setSavingCapabilities(true);
    try {
      const res = await axios.put(
        `${API_CONFIG.core}/api/admin/agents/${selectedAgent.id}`,
        { capabilities: editedCapabilities },
        { headers: { 'x-superadmin-key': ADMIN_KEY } }
      );
      if (res.data.success) {
        toast.success(`Capabilities updated for ${selectedAgent.name}`);
        const updated = await agentService.getById(selectedAgent.id);
        setSelectedAgent(updated);
        // Also refresh the agent list so sidebar reflects changes
        await loadAgents();
        setIsEditingCapabilities(false);
        setNewCapability('');
      } else {
        throw new Error(res.data.error);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save capabilities');
    } finally {
      setSavingCapabilities(false);
    }
  };

  const selectAgent = async (agent: Agent) => {
    setLoading(true);
    try {
      const fullAgent = await agentService.getById(agent.id);
      setSelectedAgent(fullAgent);
      setActiveTab('overview');
      
      // Clear messages when switching agents
      setMessages([]);
      // Reset OCR state
      setOcrText(null);
      setOcrConfirmed(false);
      setOcrPreview(null);
      setOcrAnalysis(null);
      // Reset Pro state
      setProMessages([]);
      setProInput('');
      setProStatus('');
      // Add welcome message
      setMessages([{
        id: Date.now().toString(),
        role: 'assistant',
        content: `Hello! I'm ${fullAgent.name}. ${fullAgent.description}. How can I help you today?`,
        timestamp: new Date(),
      }]);
    } catch (error) {
      toast.error('Failed to load agent details');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !selectedAgent || sending) return;

    const currentInput = input;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: currentInput,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSending(true);

    // Create a placeholder assistant message that we'll stream into
    const assistantMsgId = (Date.now() + 1).toString();
    setMessages(prev => [...prev, {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    }]);

    try {
      let fullResponse = '';

      await queryService.sendQueryStream(
        {
          userId: '9223037474',
          sessionId: sessionId.current,
          query: currentInput,
          agentId: selectedAgent.id,
          context: ocrConfirmed && ocrText ? { ocrText } : undefined,
        },
        // onChunk — append text as it streams in
        (text) => {
          fullResponse += text;
          setMessages(prev => prev.map(m =>
            m.id === assistantMsgId ? { ...m, content: fullResponse } : m
          ));
        },
        // onStatus
        undefined,
        // onDone
        async (metadata) => {
          // Log query for billing
          try {
            await queryService.logQuery({
              userId: '9223037474',
              sessionId: sessionId.current,
              query: currentInput,
              response: fullResponse,
              tokens: metadata?.billing?.tokensCharged || 0,
              model: metadata?.metadata?.model || 'external-api',
              agentId: selectedAgent.id,
            });
          } catch (e) {
            console.warn('Failed to log query:', e);
          }
        },
        // onError
        (errorMsg) => {
          setMessages(prev => prev.map(m =>
            m.id === assistantMsgId ? { ...m, content: `⚠️ ${errorMsg}` } : m
          ));
        }
      );

      // If no text came through, show an error
      if (!fullResponse) {
        setMessages(prev => prev.map(m =>
          m.id === assistantMsgId ? { ...m, content: '⚠️ No response received. Please try again.' } : m
        ));
      }
    } catch (error: any) {
      const msg = error?.message || '';
      const displayMsg = msg.toLowerCase().includes('rate limit')
        ? '⏳ Rate limit reached. Please wait a minute and try again.'
        : msg.toLowerCase().includes('timed out')
        ? '⏱️ Request timed out. Please try again.'
        : '⚠️ Failed to get response. Please try again.';

      setMessages(prev => prev.map(m =>
        m.id === assistantMsgId ? { ...m, content: displayMsg } : m
      ));
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const clearChat = () => {
    if (selectedAgent) {
      setMessages([{
        id: Date.now().toString(),
        role: 'assistant',
        content: `Hello! I'm ${selectedAgent.name}. ${selectedAgent.description}. How can I help you today?`,
        timestamp: new Date(),
      }]);
      setOcrText(null);
      setOcrConfirmed(false);
      setOcrPreview(null);
      setOcrAnalysis(null);
    }
  };

  const handleProSend = async () => {
    if (!proInput.trim() || !selectedAgent || proSending) return;

    const currentInput = proInput;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: currentInput,
      timestamp: new Date(),
    };

    setProMessages(prev => [...prev, userMessage]);
    setProInput('');
    setProSending(true);
    setProStatus('');

    const assistantMsgId = (Date.now() + 1).toString();
    setProMessages(prev => [...prev, {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    }]);

    try {
      let fullResponse = '';

      await queryService.sendProQueryStream(
        {
          userId: '9223037474',
          sessionId: sessionId.current,
          query: currentInput,
          agentId: selectedAgent.id,
        },
        // onChunk
        (text) => {
          fullResponse += text;
          setProMessages(prev => prev.map(m =>
            m.id === assistantMsgId ? { ...m, content: fullResponse } : m
          ));
        },
        // onStatus
        undefined,
        // onRouting
        undefined,
        // onDone
        async () => {
          setProStatus('');
        },
        // onError
        (errorMsg) => {
          setProMessages(prev => prev.map(m =>
            m.id === assistantMsgId ? { ...m, content: `⚠️ ${errorMsg}` } : m
          ));
        }
      );

      if (!fullResponse) {
        setProMessages(prev => prev.map(m =>
          m.id === assistantMsgId ? { ...m, content: '⚠️ No response received. Please try again.' } : m
        ));
      }
    } catch (error: any) {
      const msg = error?.message || '';
      const displayMsg = msg.toLowerCase().includes('rate limit')
        ? '⏳ Rate limit reached. Please wait a minute and try again.'
        : '⚠️ Failed to get Pro response. Please try again.';

      setProMessages(prev => prev.map(m =>
        m.id === assistantMsgId ? { ...m, content: displayMsg } : m
      ));
    } finally {
      setProSending(false);
      setProStatus('');
    }
  };

  const clearProChat = () => {
    setProMessages([]);
    setProStatus('');
  };

  const handleOcrUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    setOcrProcessing(true);
    setOcrText(null);
    setOcrConfirmed(false);
    setOcrAnalysis(null);

    const reader = new FileReader();
    reader.onload = () => setOcrPreview(reader.result as string);
    reader.readAsDataURL(file);

    try {
      const base64 = await new Promise<string>((resolve) => {
        const r = new FileReader();
        r.onload = () => {
          const result = r.result as string;
          resolve(result.split(',')[1]);
        };
        r.readAsDataURL(file);
      });

      const res = await fetch(`${CORE_API}/api/ocr/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: base64, mimeType: file.type, outputFormat: 'json', language: 'auto', forceStrategy: 'HYBRID' }),
      });
      const data = await res.json();
      if (data.success !== false && data.text) {
        setOcrText(data.text);
        setOcrAnalysis(data.analysis || null);
        toast.success('Document text extracted');
      } else {
        toast.error(data.error || 'OCR extraction failed');
      }
    } catch (err: any) {
      toast.error('OCR error: ' + err.message);
    } finally {
      setOcrProcessing(false);
    }
  }, [CORE_API]);

  const handleOcrConfirm = () => {
    setOcrConfirmed(true);
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: 'assistant' as const,
      content: `📄 Document loaded and confirmed. You can now ask questions about this document.\n\nExtracted text preview:\n"${(ocrText || '').slice(0, 300)}${(ocrText || '').length > 300 ? '...' : ''}"`,
      timestamp: new Date(),
    }]);
    toast.success('Document confirmed — ask your questions');
  };

  const handleOcrReject = () => {
    setOcrText(null);
    setOcrConfirmed(false);
    setOcrPreview(null);
    setOcrAnalysis(null);
    toast('Document rejected. Upload again if needed.');
  };

  const copyWidgetCode = async () => {
    const widgetCode = generateWidgetCode();
    try {
      await navigator.clipboard.writeText(widgetCode);
      setCopied(true);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  const generateWidgetCode = () => {
    return `<!-- JoiLaw AI Chat Widget -->
<!-- Add this to your website to embed AI legal chat -->
<!-- Requires: valid API key (get from your Launchpad admin) -->

<script src="https://vidhiguruapi.vidhiguru.in/widget.js"
  data-agent="${selectedAgent?.id || 'junior'}"
  data-api-key="YOUR_API_KEY"
  data-position="bottom-right"
  data-theme="light">
</script>

<!--
  ╔══════════════════════════════════════════╗
  ║  CONFIGURATION OPTIONS                   ║
  ╠══════════════════════════════════════════╣
  ║  data-agent     : Agent ID              ║
  ║  data-api-key   : Your client API key   ║
  ║  data-position  : bottom-right (default)║
  ║                   bottom-left           ║
  ║  data-theme     : light | dark          ║
  ║  data-lang      : en | mr | hi          ║
  ║  data-title     : Custom widget title   ║
  ╚══════════════════════════════════════════╝

  IMPORTANT:
  • Your API key must be whitelisted for this domain
  • Contact your admin to add your domain to allowed origins
  • Tokens are deducted from your account per query
  • Widget supports: text chat, voice, OCR

  ALTERNATIVE: REST API Integration
  ─────────────────────────────────
  curl -X POST https://vidhiguruapi.vidhiguru.in/api/v1/agents/${selectedAgent?.id || 'junior'}/chat/stream \\
    -H "x-api-key: YOUR_API_KEY" \\
    -H "Content-Type: application/json" \\
    -d '{"query": "Your legal question here"}'
-->`;
  };

  return (
    <div className="flex flex-col h-screen">
      <Header 
        title="Agent Explorer" 
        subtitle="Explore AI agents with their system instructions"
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Agent List */}
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Available Agents
            </h3>
            {agents.map((agent) => (
              <div
                key={agent.id}
                onClick={() => selectAgent(agent)}
                className={`p-4 mb-2 rounded-lg cursor-pointer transition-all ${
                  selectedAgent?.id === agent.id
                    ? 'bg-gradient-to-r from-primary/10 to-transparent border-l-4 border-primary'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xl flex-shrink-0">
                    {agent.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1 truncate">
                      {agent.name}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {agent.requiredRoles.length > 0 ? agent.requiredRoles.join(', ') : 'All Users'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
                <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                  {agent.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Agent Details */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedAgent ? (
            <>
              {/* Tabs */}
              <div className="bg-white border-b border-gray-200 px-8 flex gap-6">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors ${
                    activeTab === 'overview'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📊 Overview
                </button>
                <button
                  onClick={() => setActiveTab('instructions')}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors ${
                    activeTab === 'instructions'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📝 System Instructions
                </button>
                <button
                  onClick={() => setActiveTab('capabilities')}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors ${
                    activeTab === 'capabilities'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  ⚡ Capabilities
                </button>
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors ${
                    activeTab === 'chat'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  💬 Test Chat
                </button>
                <button
                  onClick={() => setActiveTab('pro')}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors ${
                    activeTab === 'pro'
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  ⭐ Pro Chat
                </button>
                <button
                  onClick={() => setActiveTab('voice')}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors ${
                    activeTab === 'voice'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  🎤 Test Voice
                </button>
                <button
                  onClick={() => setActiveTab('widget')}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors ${
                    activeTab === 'widget'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📦 Widget Code
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-8">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500">Loading...</div>
                  </div>
                ) : (
                  <>
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                      <div>
                        <div className="grid grid-cols-3 gap-6 mb-8">
                          <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-xl p-6 text-center">
                            <div className="text-4xl font-bold mb-2">{selectedAgent.capabilities.length}</div>
                            <div className="text-sm opacity-90">Capabilities</div>
                          </div>
                          <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-xl p-6 text-center">
                            <div className="text-4xl font-bold mb-2">
                              {selectedAgent.requiredRoles.length || 'All'}
                            </div>
                            <div className="text-sm opacity-90">User Roles</div>
                          </div>
                          <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-xl p-6 text-center">
                            <div className="text-4xl font-bold mb-2">
                              {selectedAgent.isActive ? '✅' : '❌'}
                            </div>
                            <div className="text-sm opacity-90">Status</div>
                          </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                          <h3 className="text-lg font-semibold mb-4">📋 Agent Information</h3>
                          <div className="space-y-3">
                            <div>
                              <span className="font-semibold text-gray-700">ID:</span>{' '}
                              <span className="text-gray-600">{selectedAgent.id}</span>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">Name:</span>{' '}
                              <span className="text-gray-600">{selectedAgent.name}</span>
                            </div>
                            {selectedAgent.displayNameMarathi && (
                              <div>
                                <span className="font-semibold text-gray-700">Display Name (Marathi):</span>{' '}
                                <span className="text-gray-600">{selectedAgent.displayNameMarathi}</span>
                              </div>
                            )}
                            <div>
                              <span className="font-semibold text-gray-700">Description:</span>{' '}
                              <span className="text-gray-600">{selectedAgent.description}</span>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">Default Language:</span>{' '}
                              <span className="text-gray-600">{selectedAgent.defaultLanguage || 'English'}</span>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">Required Roles:</span>{' '}
                              <span className="text-gray-600">
                                {selectedAgent.requiredRoles.length > 0
                                  ? selectedAgent.requiredRoles.join(', ')
                                  : 'None (accessible to all)'}
                              </span>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">System Agent:</span>{' '}
                              <span className="text-gray-600">{selectedAgent.isSystemAgent ? 'Yes' : 'No'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* System Instructions Tab */}
                    {activeTab === 'instructions' && (
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <div className="mb-4 p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg">
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <span className="text-2xl">{selectedAgent.icon}</span>
                            Configuring: {selectedAgent.name}
                          </h3>
                          <p className="text-sm mt-1 opacity-90">
                            The system prompt defines this agent's persona. All queries are processed through the external API with this prompt.
                          </p>
                        </div>

                        {/* Current Instructions Display */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-base font-semibold text-gray-700">
                              System Prompt
                            </h4>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-500">
                                {(selectedAgent.systemPrompt)?.length || 0} characters
                              </span>
                              {!isEditingInstructions ? (
                                <button
                                  onClick={startEditingInstructions}
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit
                                </button>
                              ) : (
                                <div className="flex gap-2">
                                  <button
                                    onClick={saveEditedInstructions}
                                    disabled={savingInstructions}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                                  >
                                    <Save className="w-4 h-4" />
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditingInstructions}
                                    disabled={savingInstructions}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                                  >
                                    <X className="w-4 h-4" />
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {isEditingInstructions ? (
                            <>
                              <textarea
                                value={editedInstructions}
                                onChange={(e) => setEditedInstructions(e.target.value)}
                                className="w-full bg-white border-2 border-blue-300 rounded-lg p-6 font-mono text-xs leading-relaxed min-h-[500px] focus:outline-none focus:border-blue-500"
                                placeholder="Enter system prompt..."
                              />
                              <div className="mt-3 text-xs text-blue-600">
                                ⚠️ This system prompt defines the agent's persona. Queries will be processed through the external API using this prompt.
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 font-mono text-xs leading-relaxed whitespace-pre-wrap max-h-[500px] overflow-y-auto">
                                {selectedAgent.systemPrompt || 'No system prompt available'}
                              </div>
                              <div className="mt-3 text-xs text-gray-500">
                                💡 This system prompt is sent with every query to define the agent's behavior. Click Edit to modify.
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Capabilities Tab */}
                    {activeTab === 'capabilities' && (
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h3 className="text-lg font-semibold">⚡ Capabilities</h3>
                            <p className="text-sm text-gray-500 mt-1">
                              Toggle capabilities to control what features this agent supports
                            </p>
                          </div>
                          {!isEditingCapabilities ? (
                            <button
                              onClick={startEditingCapabilities}
                              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit Capabilities
                            </button>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={saveCapabilities}
                                disabled={savingCapabilities}
                                className="flex items-center gap-2 px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                              >
                                {savingCapabilities ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save
                              </button>
                              <button
                                onClick={cancelEditingCapabilities}
                                disabled={savingCapabilities}
                                className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>

                        {isEditingCapabilities ? (
                          <div className="space-y-6">
                            {/* Quick toggles for well-known capabilities */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Toggles</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {KNOWN_CAPABILITIES.map((cap) => {
                                  const isEnabled = editedCapabilities.includes(cap.id);
                                  return (
                                    <div
                                      key={cap.id}
                                      onClick={() => toggleCapability(cap.id)}
                                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        isEnabled
                                          ? 'border-green-400 bg-green-50'
                                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                      }`}
                                    >
                                      <div className="text-2xl">{cap.icon}</div>
                                      <div className="flex-1">
                                        <div className="font-semibold text-gray-900 text-sm">{cap.label}</div>
                                        <div className="text-xs text-gray-500">{cap.description}</div>
                                      </div>
                                      {isEnabled ? (
                                        <ToggleRight className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Current capabilities list (editable) */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-3">All Active Capabilities</h4>
                              <div className="flex flex-wrap gap-2 mb-4">
                                {editedCapabilities.length === 0 && (
                                  <p className="text-sm text-gray-400 italic">No capabilities selected</p>
                                )}
                                {editedCapabilities.map((cap, i) => (
                                  <span
                                    key={i}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium border border-indigo-200"
                                  >
                                    {cap}
                                    <button
                                      onClick={() => removeCapability(cap)}
                                      className="text-indigo-400 hover:text-red-500 transition-colors"
                                    >
                                      <X size={14} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Add custom capability */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Add Custom Capability</h4>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={newCapability}
                                  onChange={(e) => setNewCapability(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && addCustomCapability()}
                                  placeholder="e.g. litigation-strategy, arbitration..."
                                  className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary transition-colors"
                                />
                                <button
                                  onClick={addCustomCapability}
                                  disabled={!newCapability.trim()}
                                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                >
                                  <Plus size={16} /> Add
                                </button>
                              </div>
                            </div>

                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-xs text-amber-700">
                                💡 <strong>voice</strong> enables the Voice tab and voice mode in Chat. <strong>ocr</strong> enables document upload and OCR extraction. Changes are saved to the agent config and reflected immediately in the Chat interface.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {selectedAgent.capabilities.map((capability, index) => {
                              const known = KNOWN_CAPABILITIES.find(k => k.id === capability);
                              return (
                                <div
                                  key={index}
                                  className="bg-gray-50 p-4 rounded-lg border-l-4 border-primary"
                                >
                                  <div className="text-2xl mb-2">{known?.icon || '✨'}</div>
                                  <div className="font-semibold text-gray-900">{capability}</div>
                                  {known && <div className="text-xs text-gray-500 mt-1">{known.description}</div>}
                                </div>
                              );
                            })}
                            {selectedAgent.capabilities.length === 0 && (
                              <div className="col-span-full text-center py-8 text-gray-400">
                                <p className="text-lg mb-2">No capabilities configured</p>
                                <p className="text-sm">Click "Edit Capabilities" to add some</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Chat Tab */}
                    {activeTab === 'chat' && (
                      <div className="bg-white rounded-xl shadow-sm p-6 h-full flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">💬 Test Conversation</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Chat with {selectedAgent.name} to see how system instructions affect responses
                            </p>
                          </div>
                          <button
                            onClick={clearChat}
                            className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                          >
                            🗑️ Clear
                          </button>
                        </div>

                        {/* OCR Upload — optional for OCR-capable agents */}
                        {hasOcr && !ocrConfirmed && (ocrProcessing || ocrText) && (
                          <div className="mb-4 p-3 border border-indigo-200 bg-indigo-50/50 rounded-xl">
                            {ocrProcessing && (
                              <div className="flex items-center justify-center gap-3 py-4">
                                <Loader2 className="animate-spin text-indigo-500" size={20} />
                                <span className="text-sm text-indigo-600">Extracting text from document...</span>
                              </div>
                            )}

                            {ocrText && !ocrConfirmed && (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Original document */}
                                  <div>
                                    <p className="text-[10px] font-semibold text-gray-400 mb-1">Original Document</p>
                                    {ocrPreview && <img src={ocrPreview} alt="Uploaded" className="w-full max-h-64 object-contain rounded-lg border bg-white" />}
                                  </div>
                                  {/* Editable extracted text */}
                                  <div className="flex flex-col">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-[10px] font-semibold text-gray-400">Extracted Text (editable)</p>
                                      {ocrAnalysis && (
                                        <div className="flex gap-1">
                                          {ocrAnalysis.documentType && <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">{ocrAnalysis.documentType}</span>}
                                          {ocrAnalysis.strategy && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">{ocrAnalysis.strategy}</span>}
                                          {ocrAnalysis.confidence && <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">{Math.round(ocrAnalysis.confidence * 100)}%</span>}
                                        </div>
                                      )}
                                    </div>
                                    <textarea
                                      value={ocrText}
                                      onChange={e => setOcrText(e.target.value)}
                                      className="flex-1 w-full bg-white rounded-lg p-2 border text-xs text-gray-800 font-mono resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300 min-h-[200px]"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <p className="text-xs text-gray-500 flex-1">Review and edit the text if needed, then confirm to use as context.</p>
                                  <button onClick={handleOcrConfirm} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700">
                                    <CheckCircle size={14} /> Confirm
                                  </button>
                                  <button onClick={handleOcrReject} className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200">
                                    <XCircle size={14} /> Reject
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {ocrConfirmed && (
                          <div className="mb-4 flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                            <FileText className="text-green-600" size={16} />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-green-800">Document loaded</p>
                              <p className="text-[10px] text-green-600">{ocrText?.length.toLocaleString()} chars — queries use this as context</p>
                            </div>
                            <button onClick={() => { setOcrText(null); setOcrConfirmed(false); setOcrPreview(null); setOcrAnalysis(null); }} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                              <Trash2 size={12} /> Clear
                            </button>
                          </div>
                        )}

                        {/* Messages */}
                        <div className="flex-1 bg-gray-50 rounded-xl p-6 mb-4 overflow-y-auto min-h-[400px] max-h-[500px]">
                          {messages.map((message) => (
                            <ChatMessage key={message.id} message={message} />
                          ))}
                          {sending && (
                            <div className="flex gap-3 mb-4">
                              <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                              </div>
                              <div className="bg-white shadow-sm border border-gray-200 rounded-2xl px-4 py-3">
                                <div className="text-sm text-gray-600">Thinking...</div>
                              </div>
                            </div>
                          )}
                          <div ref={messagesEndRef} />
                        </div>

                        {/* Input — always enabled, OCR is optional */}
                        <div className="flex gap-3">
                          {hasOcr && !ocrConfirmed && !ocrProcessing && !ocrText && (
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="px-3 py-3 border-2 border-indigo-200 text-indigo-600 rounded-xl hover:bg-indigo-50 transition-colors flex items-center gap-1"
                              title="Upload document (optional)"
                            >
                              <Upload size={18} />
                            </button>
                          )}
                          <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            placeholder={ocrConfirmed ? 'Ask a question about the document...' : 'Ask a question to test the agent...'}
                            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors"
                            disabled={sending}
                          />
                          <button
                            onClick={handleSend}
                            disabled={sending || !input.trim()}
                            className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            Send
                          </button>
                          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleOcrUpload(f); }} />
                        </div>
                      </div>
                    )}

                    {/* Pro Chat Tab */}
                    {activeTab === 'pro' && (
                      <div className="bg-white rounded-xl shadow-sm p-6 h-full flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              ⭐ Pro Chat
                              <span className="text-xs font-bold px-2 py-0.5 bg-gradient-to-r from-amber-400 to-amber-600 text-white rounded-full">
                                PRO
                              </span>
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Chat with {selectedAgent.name} in Pro mode
                            </p>
                          </div>
                          <button
                            onClick={clearProChat}
                            className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                          >
                            🗑️ Clear
                          </button>
                        </div>

                        {/* Pro Messages */}
                        <div className="flex-1 bg-amber-50/30 rounded-xl p-6 mb-4 overflow-y-auto min-h-[400px] max-h-[500px] border border-amber-100">
                          {proMessages.length === 0 && (
                            <div className="flex items-center justify-center h-full text-gray-500">
                              <div className="text-center">
                                <div className="text-4xl mb-2">⭐</div>
                                <p className="font-medium">Pro Mode</p>
                                <p className="text-sm text-gray-400 mt-1">Enhanced responses with Pro model</p>
                              </div>
                            </div>
                          )}
                          {proMessages.map((message) => (
                            <ChatMessage key={message.id} message={message} />
                          ))}
                          {proSending && (
                            <div className="flex gap-3 mb-4">
                              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                                <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                              </div>
                              <div className="bg-white shadow-sm border border-amber-200 rounded-2xl px-4 py-3">
                                <div className="text-sm text-amber-700">Thinking...</div>
                              </div>
                            </div>
                          )}
                          <div ref={proMessagesEndRef} />
                        </div>

                        {/* Pro Input */}
                        <div className="flex gap-3">
                          <input
                            type="text"
                            value={proInput}
                            onChange={(e) => setProInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleProSend()}
                            placeholder="Ask a question using Pro model..."
                            className="flex-1 px-4 py-3 border-2 border-amber-200 rounded-xl focus:outline-none focus:border-amber-400 transition-colors"
                            disabled={proSending}
                          />
                          <button
                            onClick={handleProSend}
                            disabled={proSending || !proInput.trim()}
                            className="px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {proSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            Pro
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Voice Tab */}
                    {activeTab === 'voice' && (
                      <div className="bg-white rounded-xl shadow-sm h-full flex flex-col">
                        <GeminiVoiceChat 
                          agent={selectedAgent} 
                          sessionId={sessionId.current} 
                        />
                      </div>
                    )}

                    {/* Widget Code Tab */}
                    {activeTab === 'widget' && (
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">📦 Widget Embed Code</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              One-line script tag to add AI chat to any website
                            </p>
                          </div>
                          <button
                            onClick={copyWidgetCode}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                          >
                            {copied ? (
                              <>
                                <Check className="w-5 h-5" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-5 h-5" />
                                Copy Code
                              </>
                            )}
                          </button>
                        </div>

                        <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto max-h-[600px] overflow-y-auto">
                          <pre className="text-sm text-gray-300 font-mono leading-relaxed">
                            <code>{generateWidgetCode()}</code>
                          </pre>
                        </div>

                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">📝 How to Use:</h4>
                          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                            <li>Replace <code className="bg-blue-100 px-2 py-1 rounded">YOUR_API_KEY</code> with your client API key</li>
                            <li>Add your domain to the allowed origins (ask admin)</li>
                            <li>Paste the script tag into your HTML before <code className="bg-blue-100 px-2 py-1 rounded">&lt;/body&gt;</code></li>
                            <li>A chat bubble appears on your website — click to open</li>
                          </ol>
                        </div>

                        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-semibold text-green-900 mb-2">✨ Features:</h4>
                          <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                            <li>One script tag — no build tools or dependencies</li>
                            <li>Text chat with streaming AI responses</li>
                            <li>Voice conversation (Gemini Live)</li>
                            <li>OCR — scan documents from chat</li>
                            <li>Domain-restricted API key for security</li>
                            <li>Tokens auto-deducted from your account</li>
                          </ul>
                        </div>

                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-semibold text-yellow-900 mb-2">⚙️ Configuration:</h4>
                          <div className="text-sm text-yellow-800 space-y-2">
                            <p><strong>API Base:</strong> <code className="bg-yellow-100 px-2 py-1 rounded">https://vidhiguruapi.vidhiguru.in</code></p>
                            <p><strong>Agent ID:</strong> <code className="bg-yellow-100 px-2 py-1 rounded">{selectedAgent?.id}</code></p>
                            <p className="mt-2 text-xs text-red-600 font-semibold">⚠️ Your domain must be whitelisted for the API key to work. Contact admin to add your domain.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="text-6xl mb-4">👈</div>
                <h3 className="text-xl font-semibold mb-2">Select an agent to view details</h3>
                <p className="text-sm">Choose an agent from the left panel to explore its configuration</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentExplorerPage;
